'use client'

import { useNewsletterSignup } from '@/lib/c2c/newsletter/use-newsletter-signup'

/**
 * The store keys this section's chrome renders. The heading is two keys, not one with a
 * baked line break: they are two clauses (hook, then payoff), and a break tuned to
 * English would fall in the wrong place everywhere else.
 */
type NewsletterLabelKey =
    | 'newsletterSectionHeader'
    | 'newsletterSectionSubheader'
    | 'newsletterSectionText'
    | 'newsletterSectionImageAlt'
    | 'newsletterEmailPlaceholder'
    | 'newsletterSubscribeButton'
    | 'newsletterInvalidEmail'
    | 'newsletterSuccess'
    | 'newsletterError'

interface NewsletterSubscriptionProps {
    staticBaseUrl: string
    originCity: string
    destinationCity: string
    locale: string
    transportMode: string
    /** Section chrome, keyed by the translation-store key each carries. */
    labels?: Partial<Record<NewsletterLabelKey, string>>
}

export function NewsletterSubscription({
    staticBaseUrl,
    originCity,
    destinationCity,
    locale,
    transportMode,
    labels,
}: NewsletterSubscriptionProps) {
    const chikuImg = `${staticBaseUrl}static/media/chiku-newsletter.43def637.png`
    const { email, setEmail, status, validationError, isBusy, handleSubmit } = useNewsletterSignup({
        locale,
        subscriptionSource: 'landing_c2c',
        originCity,
        destinationCity,
        transportMode,
    })

    const header = labels?.newsletterSectionHeader
    const subheader = labels?.newsletterSectionSubheader
    const placeholder = labels?.newsletterEmailPlaceholder
    const submitLabel = labels?.newsletterSubscribeButton
    const invalidEmailLabel = labels?.newsletterInvalidEmail
    const errorLabel = labels?.newsletterError

    // The placeholder is this field's only label and the button its only affordance, and
    // the two error strings are the form's only way to say it rejected or failed — without
    // any of the four the form is a mystery box: unlabeled controls, or a submit that
    // appears to do nothing on a bad address. Drop the section rather than serve one.
    // Everything else degrades to its own element going missing.
    if (!placeholder || !submitLabel || !invalidEmailLabel || !errorLabel) {
        return null
    }

    return (
        <section className="my-[72px] md:my-24" data-section="newsletter" id="newsletter-section">
            <div className="mx-auto flex max-w-[1056px] flex-wrap items-center justify-center gap-8 px-4 md:flex-row-reverse md:flex-nowrap md:justify-between md:gap-4 lg:gap-12">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                    // The mascot is decoration: with no store copy it goes unnamed rather
                    // than announcing an English name on a localized page.
                    alt={labels?.newsletterSectionImageAlt ?? ''}
                    className="w-[400px]"
                    height={347}
                    loading="lazy"
                    src={chikuImg}
                    width={400}
                />

                <form
                    className="flex w-full flex-col gap-8 md:max-w-[620px]"
                    onSubmit={(e) => void handleSubmit(e)}
                    noValidate
                >
                    {(header ?? subheader) && (
                        <h2 className="text-2xl font-bold text-[#00606b] md:text-[32px] md:leading-[40px]">
                            {header}
                            {header && subheader && <br />}
                            {subheader}
                        </h2>
                    )}

                    {labels?.newsletterSectionText && (
                        <p className="text-[#2e3033]">{labels.newsletterSectionText}</p>
                    )}

                    <div>
                        <input
                            aria-invalid={validationError ? 'true' : undefined}
                            className="w-full rounded-[4px] border border-[#bbbbbb] bg-white px-4 py-3"
                            disabled={isBusy}
                            name="emailAddress"
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder={placeholder}
                            type="email"
                            value={email}
                        />
                        {validationError && (
                            <p className="mt-2 text-sm text-red-600" role="alert">
                                {invalidEmailLabel}
                            </p>
                        )}
                    </div>

                    <div className="flex items-center gap-6">
                        <button
                            className="rounded-[7px] border-2 border-transparent bg-[#007380] px-6 py-[14px] text-sm leading-[20px] font-semibold tracking-[2px] text-white uppercase hover:bg-[#009aab] disabled:opacity-50"
                            disabled={isBusy}
                            type="submit"
                        >
                            {submitLabel}
                        </button>
                        {status === 'success' && labels?.newsletterSuccess && (
                            <span className="text-sm font-bold text-[#007380]">
                                {labels.newsletterSuccess}
                            </span>
                        )}
                        {status === 'error' && (
                            <span className="text-sm font-bold text-red-600" role="alert">
                                {errorLabel}
                            </span>
                        )}
                    </div>
                </form>
            </div>
        </section>
    )
}
